/**
 * HQ area registry — the ONE canonical list of top-level product areas.
 *
 * Client- AND edge-safe on purpose (no React, no icons, no `server-only`): the
 * SAME entries drive the sidebar nav (client), route/access gating (edge
 * middleware), and breadcrumbs (client), so nav, routes, and chrome can never
 * disagree. Sidebar icons + sub-items are layered on top in
 * `components/layout/nav-areas.ts`, which imports from here.
 *
 * To add a top-level area: add an entry to `AREAS` below, then register its
 * sidebar section in `nav-areas.ts`. `AreaId` widens automatically, and
 * middleware gating + breadcrumbs pick the area up — no edits to `middleware.ts`,
 * `app-sidebar.tsx`, or `app-layout.tsx`.
 *
 * HQ-native; never part of the lsm_proto drift-sync.
 */
import type { SignalAccess } from '@/lib/signal/access'

/**
 * Who can see and reach an area, resolved against the existing access roles (see
 * `areaVisibleForRole`). Two kinds of "later" change:
 *
 *  - Point an area at a DIFFERENT existing audience (e.g. flip Commerce Hub from
 *    `internal-only` to `all-customers` when it ships): change the one
 *    `visibility` value on its `AREAS` entry. That is the one-line change.
 *  - Introduce a genuinely NEW user role (e.g. a Commerce-pilot merchant who
 *    should see ONLY Commerce) — that role does not exist today. Wiring it is a
 *    small, well-defined change, not one line: add it to `SignalAccess` + its
 *    resolvers in `lib/signal/access.ts`, add it to `VALID_ROLES` in
 *    `middleware.ts`, then add a variant here + one branch in
 *    `areaVisibleForRole`.
 */
export type AreaVisibility =
  | 'signal' // internal staff + Signal-pilot merchants (the Signal incumbent)
  | 'internal-only' // under construction: Lula staff only (Commerce Hub today)
  | 'all-customers' // internal staff + non-Signal customers (future merchant areas)

export interface AreaRoute {
  /** Stable slug; `AreaId` is derived from the ids actually in `AREAS`. */
  id: string
  /** Sidebar title + breadcrumb root label. */
  label: string
  /** The area's root route, e.g. '/signal'. */
  basePath: string
  /** Other path prefixes owned by this area (APIs, sibling routes, redirects). */
  extraPrefixes?: readonly string[]
  visibility: AreaVisibility
}

export const AREAS = [
  {
    id: 'signal',
    label: 'Signal',
    basePath: '/signal',
    extraPrefixes: ['/api/signal'],
    visibility: 'signal',
  },
  {
    id: 'commerce',
    label: 'Commerce Hub',
    basePath: '/commerce',
    // /merchandising = the surface editors + server-redirects into /commerce; the two
    // API routes back the merch editors' AI-assist (assist) + banner copy (banners).
    // /api/commerce/orders = the Orders read proxy (LUL-7226).
    extraPrefixes: ['/merchandising', '/api/merchandising', '/api/banners', '/api/commerce/orders'],
    // Under construction: Lula staff only. Flip to its real audience at ship (one line).
    visibility: 'internal-only',
  },
] as const satisfies readonly AreaRoute[]

/**
 * The set of area ids, DERIVED from `AREAS` so the sidebar nav and the routing
 * table can never drift apart — a `NavItem.id` must be an id that actually
 * exists here. Add an `AREAS` entry above and its id is available automatically.
 */
export type AreaId = (typeof AREAS)[number]['id']

/**
 * Does a role see/reach an area? The single source of truth for BOTH sidebar
 * show/hide and middleware gating, so the nav and the URL never disagree.
 */
export function areaVisibleForRole(vis: AreaVisibility, role: SignalAccess): boolean {
  switch (vis) {
    case 'signal':
      return role === 'internal' || role === 'signal-only'
    case 'internal-only':
      return role === 'internal'
    case 'all-customers':
      return role === 'internal' || role === 'no-signal'
    default:
      return false
  }
}

/** The area that owns a pathname (prefix-safe match), or null. */
export function areaForPath(pathname: string): AreaRoute | null {
  const owns = (prefix: string) => pathname === prefix || pathname.startsWith(prefix + '/')
  return AREAS.find((a) => owns(a.basePath) || (a.extraPrefixes ?? []).some(owns)) ?? null
}
